import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from 'app/AppStore';
import { SDivider } from 'components/Login/LoginForm/LoginForm.styled';
import {
  getEstimateB2BDiscounts,
  getEstimateB2BPrices,
  getRealEstimateReferencesCount,
  getReferences,
} from 'domains/estimate/Estimate.store';
import { getCurrency } from 'domains/user';
import {
  getTotalDiscountPrice,
  getTotalVatExclPriceForClient,
  getTotalVatInclPriceForClient,
  getTotalVatPriceForClient,
} from 'pages/EstimatePage/OrderEstimate/B2BEstimateOrderValidationStep';
import { Flex, LightCard, SPACE_BETWEEN, Text } from 'UI';
import { getData } from 'utils';
import { textFormatter } from 'utils/format';

export interface TotalSectionProps {
  estimateId: string;
}

const SECTION_BOLD = 'section_bold';

const TotalSection = ({ estimateId }: TotalSectionProps) => {
  const { t } = useTranslation();
  const numberOfItems = useSelector((state: RootState) => getRealEstimateReferencesCount(state, estimateId));
  const references = getData(useSelector((state: RootState) => getReferences(state, estimateId))) ?? [];
  const allItems = [...references];

  const prices = useSelector((state: RootState) => getEstimateB2BPrices(state, estimateId));
  const discounts = useSelector((state: RootState) => getEstimateB2BDiscounts(state, estimateId));

  const totalVatExclPrice = getTotalVatExclPriceForClient(allItems, prices, discounts);
  const totalVatInclPrice = getTotalVatInclPriceForClient(allItems, prices, discounts);
  const totalVatPrice = getTotalVatPriceForClient(allItems, prices, discounts);
  const totalDiscountPrice = getTotalDiscountPrice(allItems, prices, discounts);
  const currency = useSelector(getCurrency);
  return (
    <LightCard
      title={<Trans i18nKey="estimate.pdf.estimate_total_items">Estimate total: {{ numberOfItems }} items</Trans>}
      data-cy={'total-order-items-count'}
    >
      <Flex justify={SPACE_BETWEEN}>
        <Text type={SECTION_BOLD}>{`${t('common.price.total_VAT_excl', 'Total VAT. Excl')}:`}</Text>
        <Text type="base" data-cy={'total-vat-excl'}>
          {textFormatter.formatCurrency(totalVatExclPrice.getValue(), currency)}
        </Text>
      </Flex>
      <Flex justify={SPACE_BETWEEN}>
        <Text type={SECTION_BOLD}>{t('common.price.vat', 'VAT:')}</Text>
        <Text type="base" data-cy={'vat-number'}>
          {textFormatter.formatCurrency(totalVatPrice.getValue(), currency)}
        </Text>
      </Flex>
      {Number(totalDiscountPrice) > 0 && (
        <Flex justify={SPACE_BETWEEN}>
          <Text type={SECTION_BOLD}>{`${t('common.discount', 'Discount')}:`}</Text>
          <Text type="base">{textFormatter.formatCurrency(totalDiscountPrice, currency)}</Text>
        </Flex>
      )}
      <SDivider />
      <Flex justify={SPACE_BETWEEN}>
        <Text type={SECTION_BOLD}>{`${t('common.price.total_vat_incl', 'Total VAT. Incl')}:`}</Text>
        <Text type="base" data-cy={'total-vat-incl-number'}>
          {textFormatter.formatCurrency(totalVatInclPrice.getValue(), currency)}
        </Text>
      </Flex>
    </LightCard>
  );
};

export default TotalSection;
